import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wrench, MapPin, Star, CalendarClock, MessageSquareQuote, Trash2, ClipboardList, Pencil, StickyNote, Plus, History, Ban, UserX, ClipboardEdit, ShieldCheck } from 'lucide-react';
import type { JobStatus } from '@conversia/shared-types';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import * as employeesApi from '@/api/employees.api';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { JobTimeline } from '@/components/tracking/JobTimeline';
import { EditarJobModal } from '@/components/jobs/EditarJobModal';
import { JobFormModal } from '@/components/jobs/JobFormModal';
import { JobHistoryModal } from '@/components/jobs/JobHistoryModal';
import { CancelarJobModal } from '@/components/jobs/CancelarJobModal';
import { EditarCierreModal } from '@/components/jobs/EditarCierreModal';
import { ValidarCierreModal } from '@/components/jobs/ValidarCierreModal';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { jobStatusBadge } from '@/lib/badges';
import { closureZoneLabel } from '@/lib/closure-zone';
import { esperaPendiente, groupJobs } from '@/lib/job-groups';
import { rangoDesdeInputs, toInputValue } from '@/lib/despachos-fechas';
import { cn } from '@/lib/cn';
import { useToast } from '@/hooks/useToast';

/** ¿El borrado exige la doble confirmación? (completado con opinión del cliente). */
function esBorradoProtegido(job: JobWithRelations): boolean {
  return job.estado === 'COMPLETADO' && Boolean(job.surveyResponse);
}

const estadoFilterOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'ASIGNADO', label: 'Asignado' },
  { value: 'EN_SITIO', label: 'En sitio' },
  // La cola de trabajos que el técnico cerró y esperan a la oficina.
  { value: 'PENDIENTE_VALIDACION', label: 'Por validar' },
  { value: 'COMPLETADO', label: 'Completado' },
  { value: 'NO_COMPLETADO', label: 'No completado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

// El cambio MANUAL de estado deja fuera CANCELADO y PENDIENTE_VALIDACION: los
// dos tienen su propia puerta (motivo obligatorio / cierre con cobro) y el
// backend los rechaza. Lista explícita: un `slice` se rompía en silencio en
// cuanto entraba un estado nuevo en el medio.
const estadoOptions = [
  { value: 'ASIGNADO', label: 'Asignado' },
  { value: 'EN_SITIO', label: 'En sitio' },
  { value: 'COMPLETADO', label: 'Completado' },
  { value: 'NO_COMPLETADO', label: 'No completado' },
];

/** Filtro de fecha de la cita: chips rápidos + rango a medida. */
type FechaFiltro = 'todos' | 'hoy' | 'manana' | 'rango';

const fechaChips: { value: FechaFiltro; label: string }[] = [
  { value: 'todos', label: 'Todas las fechas' },
  { value: 'hoy', label: 'Hoy' },
  { value: 'manana', label: 'Mañana' },
  { value: 'rango', label: 'Rango' },
];

/**
 * Rango [from,to] en ISO absoluto según el chip. Se calcula en la TZ del
 * navegador y se manda como instantes: el server no interpreta fechas locales.
 */
function rangoDeFiltro(
  fecha: FechaFiltro,
  desde: string,
  hasta: string,
): { from?: string; to?: string } {
  if (fecha === 'todos') return {};
  if (fecha === 'rango') {
    if (!desde || !hasta) return {};
    const r = rangoDesdeInputs(desde, hasta);
    return { from: r.from.toISOString(), to: r.to.toISOString() };
  }
  const dia = new Date();
  if (fecha === 'manana') dia.setDate(dia.getDate() + 1);
  const v = toInputValue(dia);
  const r = rangoDesdeInputs(v, v);
  return { from: r.from.toISOString(), to: r.to.toISOString() };
}

/** "2 jul, 3:30 p. m." desde el ISO de la cita, o null si no hay. */
function citaLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function Jobs() {
  const qc = useQueryClient();
  const toast = useToast();
  const [estado, setEstado] = useState<JobStatus | ''>('');
  const [employeeId, setEmployeeId] = useState('');
  const [fecha, setFecha] = useState<FechaFiltro>('todos');
  const hoyInput = toInputValue(new Date());
  const [desde, setDesde] = useState(hoyInput);
  const [hasta, setHasta] = useState(hoyInput);
  const rango = useMemo(() => rangoDeFiltro(fecha, desde, hasta), [fecha, desde, hasta]);

  const { data: tecnicos } = useQuery({
    queryKey: ['employees', 'tecnicos'],
    queryFn: () => employeesApi.listEmployees({ rol: 'TECNICO', activo: true, take: 100 }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', 'list', estado, employeeId, rango.from ?? '', rango.to ?? ''],
    queryFn: () =>
      jobsApi.listJobs({
        estado: estado || undefined,
        employeeId: employeeId || undefined,
        from: rango.from,
        to: rango.to,
        take: 100,
      }),
  });

  const assign = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      jobsApi.assignEmployee(id, value || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Técnico actualizado');
    },
    onError: () => toast.error('No se pudo asignar el técnico'),
  });

  const status = useMutation({
    mutationFn: ({ id, value }: { id: string; value: JobStatus }) =>
      jobsApi.changeStatus(id, { estado: value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Estado actualizado');
    },
    onError: () => toast.error('No se pudo cambiar el estado'),
  });

  const warranty = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) => jobsApi.setWarranty(id, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Garantía actualizada');
    },
    onError: () => toast.error('No se pudo actualizar la garantía'),
  });

  // Nuevo trabajo manual (Fase 1): venta hecha por fuera, sin pasar por el lead.
  const [nuevoOpen, setNuevoOpen] = useState(false);

  // Edición completa (modal), historial de la ficha y borrado con confirmación.
  const [editar, setEditar] = useState<JobWithRelations | null>(null);
  const [historial, setHistorial] = useState<JobWithRelations | null>(null);
  // Fase 2: cancelación (motivo obligatorio) y reporte de cierre editable.
  const [cancelar, setCancelar] = useState<JobWithRelations | null>(null);
  const [editarCierre, setEditarCierre] = useState<JobWithRelations | null>(null);
  // Trabajo que el técnico cerró y espera que la oficina cargue el cobro.
  const [validar, setValidar] = useState<JobWithRelations | null>(null);
  const [borrar, setBorrar] = useState<JobWithRelations | null>(null);
  const del = useMutation({
    mutationFn: (job: JobWithRelations) => jobsApi.deleteJob(job.id, esBorradoProtegido(job)),
    onSuccess: () => {
      setBorrar(null);
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Trabajo borrado');
    },
    onError: () => toast.error('No se pudo borrar el trabajo'),
  });

  const items = data?.items ?? [];
  const tecnicoFilterOptions = [
    { value: '', label: 'Todos los técnicos' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];
  const tecnicoAssignOptions = [
    { value: '', label: 'Sin asignar' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Trabajos"
        helpKey="jobs"
        description="Servicios agendados: asignación de técnico y estado."
        action={
          <Button onClick={() => setNuevoOpen(true)}>
            <Plus className="h-4 w-4" /> Nuevo trabajo
          </Button>
        }
      />
      <JobFormModal open={nuevoOpen} onClose={() => setNuevoOpen(false)} />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          className="w-52"
          value={estado}
          options={estadoFilterOptions}
          onChange={(e) => setEstado(e.target.value as JobStatus | '')}
        />
        <Select
          className="w-52"
          value={employeeId}
          options={tecnicoFilterOptions}
          onChange={(e) => setEmployeeId(e.target.value)}
        />
        <div className="flex items-center gap-1 rounded-lg border border-surface-border bg-white p-1 dark:border-slate-800 dark:bg-slate-900">
          {fechaChips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => setFecha(chip.value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                fecha === chip.value
                  ? 'bg-acento-lleno text-acento-sobre'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
        {fecha === 'rango' && (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
            />
            <span>→</span>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
        )}
        {fecha !== 'todos' && (
          <p className="text-xs text-slate-400">Se listan trabajos con cita en el rango.</p>
        )}
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={Wrench}
              title="Sin trabajos"
              description="Los trabajos se crean con el botón Agendar en la ficha del lead (dentro de la conversación)."
              className="m-5"
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {groupJobs(items).map((group) => [
                <li
                  key={`header-${group.key}`}
                  className="bg-surface-muted px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400"
                >
                  {group.label} · {group.jobs.length}
                </li>,
                ...group.jobs.map((job) => {
                const badge = jobStatusBadge(job.estado);
                const cliente = job.contact?.name ?? job.contact?.pushName ?? job.contact?.phone ?? '—';
                const cita = citaLabel(job.scheduledAt);
                const espera = job.estado === 'ASIGNADO' ? esperaPendiente(job.scheduledAt) : null;
                return (
                  <li key={job.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{cliente}</p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{job.descripcion}</p>
                      {cita && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500 dark:text-slate-400">
                          <CalendarClock className="h-3 w-3 shrink-0" /> Cita: {cita}
                          {espera && (
                            <span
                              className={cn(
                                'ml-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
                                espera.vencida
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                              )}
                            >
                              {espera.label}
                            </span>
                          )}
                        </p>
                      )}
                      {(job.zona || job.direccion) && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-400">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {[job.zona, job.direccion].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {/* Timeline compacta (tanda ajustes-finales): mismo
                          componente del mapa/mis-trabajos, cero lógica nueva. */}
                      <JobTimeline estado={job.estado} className="mt-1.5 max-w-[260px]" />
                      {job.notas && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500 dark:text-slate-400">
                          <StickyNote className="h-3 w-3 shrink-0" /> {job.notas}
                        </p>
                      )}
                      {(job.registroProducto || job.motivo) && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-600 dark:text-slate-300">
                          <ClipboardList className="h-3 w-3 shrink-0" />
                          {[
                            job.registroProducto ? `Registro del técnico: “${job.registroProducto}”` : null,
                            job.motivo ? `Motivo: ${job.motivo}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                      {/* No-show reportado por el técnico (Fase 2): visible mientras
                          el trabajo siga abierto — la decisión (cancelar/reagendar) es tuya. */}
                      {job.noShowReportedAt && (job.estado === 'ASIGNADO' || job.estado === 'EN_SITIO') && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs font-medium text-amber-700 dark:text-amber-400">
                          <UserX className="h-3 w-3 shrink-0" /> El técnico reportó que el cliente no llegó — cancelar o reagendar
                        </p>
                      )}
                      {(() => {
                        // Ubicación del cierre (Bloque 1): dónde ponchó Terminé/No terminé.
                        const cierre = closureZoneLabel(job);
                        if (!cierre) return null;
                        return (
                          <p
                            className={cn(
                              'mt-0.5 flex items-center gap-1 truncate text-xs',
                              cierre.enZona === true && 'text-emerald-600 dark:text-emerald-400',
                              cierre.enZona === false && 'text-danger',
                              cierre.enZona === null && 'text-slate-400',
                            )}
                          >
                            <MapPin className="h-3 w-3 shrink-0" /> {cierre.text}
                          </p>
                        );
                      })()}
                      {job.surveyResponse ? (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-600 dark:text-slate-300">
                          <MessageSquareQuote className="h-3 w-3 shrink-0" />
                          El cliente respondió: “{job.surveyResponse}”
                        </p>
                      ) : job.surveySentAt ? (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <Star className="h-3 w-3 shrink-0" /> Encuesta enviada (sin respuesta aún)
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {job.employee && <Avatar name={job.employee.nombre} src={job.employee.fotoUrl} size="sm" />}
                      <Select
                        className="w-40"
                        value={job.employeeId ?? ''}
                        options={tecnicoAssignOptions}
                        disabled={assign.isPending}
                        onChange={(e) => assign.mutate({ id: job.id, value: e.target.value })}
                      />
                    </div>
                    {job.esGarantia && <Badge variant="warning">Garantía</Badge>}
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400" title="Las garantías no generan comisión">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        checked={job.esGarantia}
                        disabled={warranty.isPending}
                        onChange={(e) => warranty.mutate({ id: job.id, value: e.target.checked })}
                      />
                      Garantía
                    </label>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    {/* En "Por validar" la salida es el botón de cierre, no el
                        desplegable: ofrecer los dos invitaría a saltarse el cobro. */}
                    {job.estado === 'PENDIENTE_VALIDACION' && (
                      <Button size="sm" onClick={() => setValidar(job)}>
                        <ShieldCheck className="mr-1 h-4 w-4" />
                        Cerrar trabajo
                      </Button>
                    )}
                    {job.estado !== 'CANCELADO' && job.estado !== 'PENDIENTE_VALIDACION' && (
                      <Select
                        className="w-44"
                        value={job.estado}
                        options={estadoOptions}
                        disabled={status.isPending}
                        onChange={(e) => status.mutate({ id: job.id, value: e.target.value as JobStatus })}
                      />
                    )}
                    {(job.estado === 'ASIGNADO' || job.estado === 'EN_SITIO') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Cancelar trabajo"
                        title="Cancelar trabajo (cliente no llegó u otra causa)"
                        className="text-slate-400 hover:text-amber-600"
                        onClick={() => setCancelar(job)}
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                    {(job.estado === 'COMPLETADO' || job.estado === 'NO_COMPLETADO') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Editar reporte de cierre"
                        title="Editar el reporte de cierre del técnico"
                        className="text-slate-400 hover:text-brand-600"
                        onClick={() => setEditarCierre(job)}
                      >
                        <ClipboardEdit className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Editar trabajo"
                      title="Editar trabajo"
                      className="text-slate-400 hover:text-brand-600"
                      onClick={() => setEditar(job)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Historial de la ficha"
                      title="Historial de la ficha (quién editó qué)"
                      className="text-slate-400 hover:text-brand-600"
                      onClick={() => setHistorial(job)}
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Borrar trabajo"
                      title="Borrar trabajo"
                      className="text-slate-400 hover:text-red-600"
                      onClick={() => setBorrar(job)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                );
                }),
              ])}
            </ul>
          )}
        </CardBody>
      </Card>

      <EditarJobModal job={editar} onClose={() => setEditar(null)} />

      <JobHistoryModal job={historial} onClose={() => setHistorial(null)} />

      <CancelarJobModal job={cancelar} onClose={() => setCancelar(null)} />

      <EditarCierreModal job={editarCierre} onClose={() => setEditarCierre(null)} />

      <ValidarCierreModal job={validar} onClose={() => setValidar(null)} />

      <Modal
        open={Boolean(borrar)}
        onClose={() => setBorrar(null)}
        title="Borrar trabajo"
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setBorrar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={del.isPending}
              onClick={() => borrar && del.mutate(borrar)}
            >
              {borrar && esBorradoProtegido(borrar) ? 'Borrar de todos modos' : 'Borrar'}
            </Button>
          </>
        }
      >
        {borrar && (
          <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
            <p>
              Se va a borrar <span className="font-medium">“{borrar.descripcion}”</span>. Esta
              acción no se puede deshacer.
            </p>
            {esBorradoProtegido(borrar) && (
              <p className="rounded-md bg-amber-50 p-2 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Este trabajo está <b>completado y tiene la opinión del cliente</b>: “
                {borrar.surveyResponse}”. Borrarlo elimina también esa opinión de las
                estadísticas.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
